import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { API_URL } from "./config";

const PEOPLE_ROWS_PER_PAGE = 200;
const RESULT_ROWS_PER_PAGE = 200;

const IdCardGenerator = () => {
  // =========================
  // Theme
  // =========================
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "rgba(25,118,210,0.95)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: BLUE,
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const checkboxAllSx = {
    color: "#fff",
    "&.Mui-checked": { color: "#fff" },
    "&.MuiCheckbox-indeterminate": { color: "#fff" },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 1,
    overflow: "hidden",
  };
  const theadRowSx = {
    backgroundColor: BLUE,
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // =========================
  // Auth / config
  // =========================
  const token = localStorage.getItem("auth");
  const [logincampusCode, setLogincampusCode] = useState("");

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const cc =
        payload?.logincampuscode ||
        payload?.campusCode ||
        payload?.CampusCode ||
        "";
      setLogincampusCode(cc);
    } catch {
      setError("Failed to decode auth token");
    }
  }, [token]);

  const authConfig = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  // =========================
  // State
  // =========================
  const [cardType, setCardType] = useState("STUDENT");
  const [templateId, setTemplateId] = useState("");
  const [validUpto, setValidUpto] = useState("");
  const [templates, setTemplates] = useState([]);

  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [loadingLookups, setLoadingLookups] = useState(false);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [loading, setLoading] = useState(false);

  const [filteredPeople, setFilteredPeople] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [personIdsInput, setPersonIdsInput] = useState("");

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const [peoplePage, setPeoplePage] = useState(0);
  const [resultPage, setResultPage] = useState(0);

  const isStudent = cardType === "STUDENT";
  const displayIdField = isStudent ? "StudentID" : "EmployeeID";
  const keyField = isStudent ? "StudentKey" : "EmployeeKey";
  const nameField = isStudent ? "FullName" : "EmployeeName";

  // =========================
  // Derived paged data
  // =========================
  const pagedPeople = useMemo(() => {
    const start = peoplePage * PEOPLE_ROWS_PER_PAGE;
    return filteredPeople.slice(start, start + PEOPLE_ROWS_PER_PAGE);
  }, [filteredPeople, peoplePage]);

  const resultRows = result?.results || [];

  const pagedResultRows = useMemo(() => {
    const start = resultPage * RESULT_ROWS_PER_PAGE;
    return resultRows.slice(start, start + RESULT_ROWS_PER_PAGE);
  }, [resultRows, resultPage]);

  const getTemplateSubCampus = (t) => t?.SubCampusCode || "";

  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.TemplateId) === String(templateId)) || null,
    [templates, templateId]
  );
  
  const findTemplateForSubCampus = (subCampusCode) => {
    const exact = templates.find(
      (t) =>
        String(t.CardType) === String(cardType) &&
        String(t.SubCampusCode || "") === String(subCampusCode || "")
    );
  
    if (exact) return exact;
  
    return (
      templates.find((t) => Number(t.IsDefault) === 1) ||
      templates[0] ||
      null
    );
  };

  // =========================
  // Load templates
  // =========================
  useEffect(() => {
    if (!logincampusCode) return;
  
    const loadTemplates = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/idcards/templates`, {
          ...authConfig,
          params: { CampusCode: logincampusCode, cardType },
        });
  
        const rows = response.data || [];
        setTemplates(rows);
  
        const currentSubCampus = filters.SubCampusCode?.[0] || "";
  
        const preferredTemplate =
          (currentSubCampus
            ? rows.find(
                (x) => String(x.SubCampusCode || "") === String(currentSubCampus)
              )
            : null) ||
          rows.find((x) => Number(x.IsDefault) === 1) ||
          rows[0] ||
          null;
  
        setTemplateId(preferredTemplate?.TemplateId || "");
  
        if (preferredTemplate?.SubCampusCode) {
          setFilters((p) => ({
            ...p,
            SubCampusCode: [preferredTemplate.SubCampusCode],
          }));
        }
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load templates");
      }
    };
  
    loadTemplates();
  }, [logincampusCode, cardType, authConfig]);
  
  // =========================
  // Load lookups
  // =========================
  useEffect(() => {
    if (!logincampusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authConfig,
          params: { CampusCode: logincampusCode },
        });

        if (cancelled) return;

        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logincampusCode, authConfig]);

  // =========================
  // Helpers
  // =========================
  const parseDisplayIds = () =>
    [
      ...new Set(
        String(personIdsInput || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
      ),
    ];

  const syncTextFromSelected = (ids) => {
    setPersonIdsInput(ids.join(", "));
  };

  const handleTogglePerson = (personId) => {
    setSelectedIds((prev) => {
      const exists = prev.includes(personId);
      const next = exists ? prev.filter((x) => x !== personId) : [...prev, personId];
      syncTextFromSelected(next);
      return next;
    });
  };

  const handleToggleAllVisible = () => {
    const visibleIds = pagedPeople.map((x) => String(x[displayIdField]));
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

    const next = allSelected
      ? selectedIds.filter((id) => !visibleIds.includes(id))
      : [...new Set([...selectedIds, ...visibleIds])];

    setSelectedIds(next);
    syncTextFromSelected(next);
  };

  const anyFilterSelected =
    filters.SubCampusCode.length > 0 ||
    filters.DegreeCode.length > 0 ||
    filters.BranchCode.length > 0 ||
    filters.SemesterCode.length > 0;

  // =========================
  // Load people from filters
  // =========================
  const handleLoadPeople = async () => {
    try {
      setListError("");
      setFilteredPeople([]);
      setPeoplePage(0);

      if (!logincampusCode) return;

      setLoadingPeople(true);

      const params = { CampusCode: logincampusCode, CardType: cardType };

      if (filters.SubCampusCode.length) params.SubCampusCode = filters.SubCampusCode;
      if (filters.DegreeCode.length) params.DegreeCode = filters.DegreeCode;
      if (filters.BranchCode.length) params.BranchCode = filters.BranchCode;
      if (filters.SemesterCode.length) params.SemesterCode = filters.SemesterCode;

      const response = await axios.get(`${API_URL}/api/idcards/bulk-people-list`, {
        ...authConfig,
        params,
      });

      const rows = response.data || [];
      setFilteredPeople(rows);

      if (!rows.length) {
        setListError(`No ${isStudent ? "students" : "employees"} found for selected filters.`);
      }
    } catch (err) {
      setListError(err?.response?.data?.error || "Failed to load records");
    } finally {
      setLoadingPeople(false);
    }
  };

  // =========================
  // Bulk generate
  // =========================

  const handleBulkGenerate = async () => {
    try {
      if (!logincampusCode) {
        setError("Campus is not loaded");
        return;
      }
  
      const enteredIds = parseDisplayIds();
  
      if (!enteredIds.length) {
        setError(`Enter at least one valid ${displayIdField}`);
        return;
      }
  
      setLoading(true);
      setError("");
      setResult(null);
      setResultPage(0);
  
      const localIdToKeyMap = new Map(
        filteredPeople
          .filter((x) => x?.[displayIdField] && x?.[keyField])
          .map((x) => [String(x[displayIdField]).trim(), Number(x[keyField])])
      );
  
      const localIdToNameMap = new Map(
        filteredPeople
          .filter((x) => x?.[displayIdField])
          .map((x) => [String(x[displayIdField]).trim(), x?.[nameField] || ""])
      );
  
      // ✅ get SubCampusCode from /bulk-people-list data
      const localIdToSubCampusMap = new Map(
        filteredPeople
          .filter((x) => x?.[displayIdField])
          .map((x) => [
            String(x[displayIdField]).trim(),
            x?.SubCampusCode ? String(x.SubCampusCode).trim() : null,
          ])
      );
  
      const matchedKeys = [];
      const unresolvedIds = [];
      const resolvedMeta = [];
  
      enteredIds.forEach((id) => {
        const cleanId = String(id).trim();
        const key = localIdToKeyMap.get(cleanId);
  
        if (key) {
          matchedKeys.push(key);
          resolvedMeta.push({
            PersonId: cleanId,
            PersonKey: key,
            PersonName: localIdToNameMap.get(cleanId) || "",
            SubCampusCode: localIdToSubCampusMap.get(cleanId) || null,
          });
        } else {
          unresolvedIds.push(cleanId);
        }
      });
  
      if (unresolvedIds.length) {
        const resolveResp = await axios.post(
          `${API_URL}/api/idcards/resolve-person-ids`,
          {
            CampusCode: logincampusCode,
            CardType: cardType,
            PersonIds: unresolvedIds,
          },
          authConfig
        );
  
        const resolvedRows = resolveResp.data?.results || [];
  
        resolvedRows.forEach((row) => {
          if (row?.found && row?.PersonKey) {
            matchedKeys.push(Number(row.PersonKey));
  
            // ✅ try to carry SubCampusCode from local bulk-people-list first
            const personId = String(row.PersonId || "").trim();
  
            resolvedMeta.push({
              PersonId: personId,
              PersonKey: Number(row.PersonKey),
              PersonName: row.PersonName || localIdToNameMap.get(personId) || "",
              SubCampusCode:
                localIdToSubCampusMap.get(personId) ||
                row.SubCampusCode ||
                null,
            });
          }
        });
      }
  
      const uniqueResolved = [];
      const seenKeys = new Set();
  
      resolvedMeta.forEach((x) => {
        const key = Number(x.PersonKey);
        if (!key || seenKeys.has(key)) return;
        seenKeys.add(key);
        uniqueResolved.push(x);
      });
  
      const uniqueKeys = uniqueResolved.map((x) => Number(x.PersonKey));
  
      if (!uniqueKeys.length) {
        setError(`None of the entered ${displayIdField}s could be mapped to ${keyField}.`);
        setLoading(false);
        return;
      }
  
      // ✅ send SubCampusCode to /generate/bulk
      const response = await axios.post(
        `${API_URL}/api/idcards/generate/bulk`,
        {
          CampusCode: logincampusCode,
          CardType: cardType,
          TemplateId: templateId || null,
          ValidUpto: validUpto || null,
          Persons: uniqueResolved.map((x) => ({
            PersonKey: Number(x.PersonKey),
            PersonId: x.PersonId || null,
            PersonName: x.PersonName || null,
            SubCampusCode: x.SubCampusCode || null,
          })),
        },
        authConfig
      );
  
      const successResults = response.data?.results || [];
      const resolvedByKey = new Map(uniqueResolved.map((x) => [Number(x.PersonKey), x]));
  
      const enrichedResults = successResults.map((row) => {
        const meta = resolvedByKey.get(Number(row.PersonKey));
        return {
          ...row,
          PersonId: row.PersonId || meta?.PersonId || "",
          PersonName: row.PersonName || meta?.PersonName || "",
          SubCampusCode: row.SubCampusCode || meta?.SubCampusCode || "",
        };
      });
  
      const unmatchedIds = enteredIds.filter(
        (id) => !uniqueResolved.some((x) => String(x.PersonId).trim() === String(id).trim())
      );
  
      setResult({
        ...response.data,
        results: enrichedResults,
        unmatchedIds,
      });
  
      setSnack({
        open: true,
        message: `Bulk generation completed. Success: ${response.data.successCount || 0}, Failed: ${response.data.failCount || 0}`,
        severity: "success",
      });
    } catch (err) {
      setError(err?.response?.data?.error || "Bulk generation failed");
    } finally {
      setLoading(false);
    }
  };

  const allVisibleSelected =
    pagedPeople.length > 0 &&
    pagedPeople.every((x) => selectedIds.includes(String(x[displayIdField])));

  const someVisibleSelected =
    pagedPeople.some((x) => selectedIds.includes(String(x[displayIdField]))) &&
    !allVisibleSelected;

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Bulk ID Card Generator
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter, select, or directly enter IDs to bulk generate student / employee ID cards
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!snack.open && (
            <Snackbar
              open
              autoHideDuration={3000}
              onClose={() => setSnack((s) => ({ ...s, open: false }))}
            >
              <Alert severity={snack.severity} sx={{ borderRadius: 2 }}>
                {snack.message}
              </Alert>
            </Snackbar>
          )}

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters / Settings
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                  gap: 2,
                  mt: 2,
                }}
              >
                <TextField
                  select
                  fullWidth
                  label="Card Type"
                  value={cardType}
                  onChange={(e) => {
                    setCardType(e.target.value);
                    setFilteredPeople([]);
                    setSelectedIds([]);
                    setPersonIdsInput("");
                    setResult(null);
                    setPeoplePage(0);
                    setResultPage(0);
                  }}
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="STUDENT">Student</MenuItem>
                  <MenuItem value="EMPLOYEE">Employee</MenuItem>
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Template"
                  value={templateId}
                  onChange={(e) => {
                    const nextTemplateId = e.target.value;
                    setTemplateId(nextTemplateId);
                
                    const tpl = templates.find(
                      (t) => String(t.TemplateId) === String(nextTemplateId)
                    );
                
                    const tplSubCampus = getTemplateSubCampus(tpl);
                
                    setFilters((p) => ({
                      ...p,
                      SubCampusCode: tplSubCampus ? [tplSubCampus] : [],
                    }));
                
                    setFilteredPeople([]);
                    setSelectedIds([]);
                    setPersonIdsInput("");
                    setResult(null);
                    setPeoplePage(0);
                    setResultPage(0);
                  }}
                  size="small"
                  sx={inputSx}
                >
                  {templates.map((t) => (
                    <MenuItem key={t.TemplateId} value={t.TemplateId}>
                      {t.TemplateName} ({t.TemplateCode})
                      {t.SubCampusCode ? ` - ${t.SubCampusCode}` : " - All"}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  type="date"
                  label="Valid Upto"
                  InputLabelProps={{ shrink: true }}
                  value={validUpto}
                  onChange={(e) => setValidUpto(e.target.value)}
                  size="small"
                  sx={inputSx}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(5, 1fr)" },
                  gap: 2,
                  mt: 2,
                }}
              >
                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) => {
                    const nextValue = value || [];
                  
                    setFilters((p) => ({
                      ...p,
                      SubCampusCode: nextValue,
                    }));
                  
                    const selectedSubCampus = nextValue[0] || "";
                    const matchingTemplate = findTemplateForSubCampus(selectedSubCampus);
                  
                    if (matchingTemplate) {
                      setTemplateId(matchingTemplate.TemplateId);
                    }
                  
                    setFilteredPeople([]);
                    setSelectedIds([]);
                    setPersonIdsInput("");
                    setResult(null);
                    setPeoplePage(0);
                    setResultPage(0);
                  }}
                  disableCloseOnSelect
                  filterSelectedOptions
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.DegreeCode}
                  value={filters.DegreeCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField {...params} label="DegreeCode(s)" size="small" sx={inputSx} />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.BranchCode}
                  value={filters.BranchCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField {...params} label="BranchCode(s)" size="small" sx={inputSx} />
                  )}
                />

                {isStudent ? (
                  <Autocomplete
                    multiple
                    options={options.SemesterCode}
                    value={filters.SemesterCode}
                    onChange={(_, value) =>
                      setFilters((p) => ({ ...p, SemesterCode: value.map(String) }))
                    }
                    disableCloseOnSelect
                    filterSelectedOptions
                    loading={loadingLookups}
                    renderInput={(params) => (
                      <TextField {...params} label="SemesterCode(s)" size="small" sx={inputSx} />
                    )}
                  />
                ) : (
                  <Box />
                )}

                <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                  <Button
                    variant="contained"
                    sx={{ ...primaryBtnSx, height: 40 }}
                    onClick={handleLoadPeople}
                    disabled={loadingPeople}
                    fullWidth
                  >
                    {loadingPeople ? "Loading..." : `Load ${isStudent ? "Students" : "Employees"}`}
                  </Button>
                </Box>
              </Box>

              {!!listError && (
                <Typography sx={{ mt: 1.5, color: "error.main", fontSize: "0.9rem" }}>
                  {listError}
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Selected {isStudent ? "Student IDs" : "Employee IDs"}
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              label={isStudent ? "StudentIDs" : "EmployeeIDs"}
              value={personIdsInput}
              onChange={(e) => {
                const raw = e.target.value;
                setPersonIdsInput(raw);
                setSelectedIds([...new Set(raw.split(",").map((x) => x.trim()).filter(Boolean))]);
              }}
              placeholder={
                isStudent
                  ? "Enter StudentIDs separated by commas"
                  : "Enter EmployeeIDs separated by commas"
              }
              sx={inputSx}
            />

            <Typography sx={{ mt: 1, fontSize: "0.82rem", color: "rgba(25,118,210,0.75)" }}>
              Enter comma-separated {displayIdField}s directly, or load/filter and add them from the
              list. Backend will automatically resolve them to {keyField}.
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                sx={primaryBtnSx}
                onClick={handleBulkGenerate}
                disabled={loading}
              >
                Bulk Generate
              </Button>

              <Button
                variant="outlined"
                sx={outlinedBtnSx}
                onClick={() => {
                  setSelectedIds([]);
                  setPersonIdsInput("");
                  setResult(null);
                  setResultPage(0);
                }}
              >
                Clear Selection
              </Button>
            </Stack>
          </Paper>

          {filteredPeople.length > 0 && (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2 }}>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ mb: 1, fontWeight: 900, color: BLUE }}>
                  {isStudent ? "Students" : "Employees"} Matching Filters
                </Typography>

                <Paper elevation={0} sx={tableWrapSx}>
                  <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                    <TableContainer component="div">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={theadRowSx}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                sx={checkboxAllSx}
                                checked={allVisibleSelected}
                                indeterminate={someVisibleSelected}
                                onChange={handleToggleAllVisible}
                              />
                            </TableCell>
                            <TableCell>{displayIdField}</TableCell>
                            <TableCell>{isStudent ? "Full Name" : "Employee Name"}</TableCell>
                            {isStudent && <TableCell>SubCampus</TableCell>}
                            {isStudent && <TableCell>Degree</TableCell>}
                            {isStudent && <TableCell>Branch</TableCell>}
                            {isStudent && <TableCell>Semester</TableCell>}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {pagedPeople.map((row, idx) => {
                            const personId = String(row[displayIdField]);
                            const checked = selectedIds.includes(personId);

                            return (
                              <TableRow
                                key={`${personId}-${idx}`}
                                sx={{ ...zebraRowSx(idx), cursor: "pointer" }}
                                onClick={() => handleTogglePerson(personId)}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    sx={checkboxSx}
                                    checked={checked}
                                    onChange={() => handleTogglePerson(personId)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </TableCell>
                                <TableCell>{row[displayIdField]}</TableCell>
                                <TableCell>{row[nameField]}</TableCell>
                                {isStudent && <TableCell>{row.SubCampusCode}</TableCell>}
                                {isStudent && <TableCell>{row.DegreeCode}</TableCell>}
                                {isStudent && <TableCell>{row.BranchCode}</TableCell>}
                                {isStudent && <TableCell>{row.SemesterCode}</TableCell>}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <TablePagination
                    component="div"
                    count={filteredPeople.length}
                    page={peoplePage}
                    onPageChange={(_, newPage) => setPeoplePage(newPage)}
                    rowsPerPage={PEOPLE_ROWS_PER_PAGE}
                    rowsPerPageOptions={[PEOPLE_ROWS_PER_PAGE]}
                  />
                </Paper>
              </Box>
            </Paper>
          )}

          {!!selectedIds.length && (
            <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mt: 2 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Selected IDs ({selectedIds.length})
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {selectedIds.map((id) => (
                  <Chip
                    key={id}
                    label={id}
                    onDelete={() => {
                      const next = selectedIds.filter((x) => x !== id);
                      setSelectedIds(next);
                      syncTextFromSelected(next);
                    }}
                    sx={{
                      mb: 1,
                      fontWeight: 800,
                      border: `1px solid rgba(240,120,40,0.18)`,
                      background: "rgba(240,120,40,0.08)",
                      color: THEME_ORANGE,
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          )}

          {result && (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2 }}>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                  Bulk Generation Result
                </Typography>

                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
                  <Chip label={`Success: ${result.successCount || 0}`} color="success" />
                  <Chip label={`Failed: ${result.failCount || 0}`} color="error" />
                  <Chip label={`Total: ${result.totalCount || 0}`} />
                </Stack>

                <Paper elevation={0} sx={tableWrapSx}>
                  <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                    <TableContainer component="div">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={theadRowSx}>
                            <TableCell>{displayIdField}</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>{keyField}</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Card Serial No</TableCell>
                            <TableCell>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pagedResultRows.map((row, idx) => (
                            <TableRow key={`${row.PersonKey}-${idx}`} sx={zebraRowSx(idx)}>
                              <TableCell>{row.PersonId}</TableCell>
                              <TableCell>{row.PersonName}</TableCell>
                              <TableCell>{row.PersonKey}</TableCell>
                              <TableCell>{row.success ? "Success" : "Failed"}</TableCell>
                              <TableCell>{row.CardSerialNo || "-"}</TableCell>
                              <TableCell>{row.error || "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <TablePagination
                    component="div"
                    count={resultRows.length}
                    page={resultPage}
                    onPageChange={(_, newPage) => setResultPage(newPage)}
                    rowsPerPage={RESULT_ROWS_PER_PAGE}
                    rowsPerPageOptions={[RESULT_ROWS_PER_PAGE]}
                  />
                </Paper>

                {!!result.unmatchedIds?.length && (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                    Unmatched IDs: {result.unmatchedIds.join(", ")}
                  </Alert>
                )}
              </Box>
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default IdCardGenerator;